import React, { FormEvent, useState } from 'react';
import { toast } from 'react-toastify';
import { Button, Input, Modal } from '../../../../../components';
import api from '../../../../../utils/api';

interface VerifyEmailModalProps {
  isOpen: boolean;
  agentUserId: string;
  onClose: () => void;
}

interface StartResponse {
  verification: {
    id: string;
    agentUserId: string;
    type: 'email';
    targetValue: string;
    status: 'pending';
    initiatedAt: string;
  };
  reissued: boolean;
  emailDelivered: boolean;
}

/**
 * Owner flow for ADR-012 email verification (#583).
 *
 * One-step from the owner's side: submit the address; server sends
 * the challenge link via Resend; the recipient clicks the link in
 * their inbox to complete. Verification lands on next page load —
 * no polling from the modal (would be wasted round-trips since
 * inbox delivery + human click take longer than reasonable poll
 * intervals; the parent re-fetches the verifications list on
 * modal close).
 *
 * Separate from VerifyEndpointModal because the flow shape is
 * genuinely different: one input + inbox-side confirm, versus
 * two-step token+host+check. Sharing a base component would
 * require a mode switch that adds complexity for no gain.
 */
export const VerifyEmailModal: React.FC<VerifyEmailModalProps> = ({
  isOpen,
  agentUserId,
  onClose,
}) => {
  const [email, setEmail] = useState('');
  const [state, setState] = useState<'idle' | 'submitting' | 'sent' | 'error'>(
    'idle',
  );
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [reissued, setReissued] = useState(false);

  const reset = () => {
    setEmail('');
    setState('idle');
    setError(null);
    setSentTo(null);
    setReissued(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      setError('Enter an email address');
      return;
    }
    setState('submitting');
    setError(null);
    try {
      const res = await api.post<StartResponse>(
        `/agents/mine/${agentUserId}/verify/email`,
        { email: trimmed },
      );
      setSentTo(res.data.verification.targetValue);
      setReissued(res.data.reissued);
      setState('sent');
      // Best-effort: emailDelivered=false doesn't fail the flow (the
      // row is still pending; owner can retry) but we surface a hint
      // so the owner doesn't assume the message is on its way.
      if (!res.data.emailDelivered) {
        toast.warning(
          "Verification row saved, but the email didn't deliver. Retry or check the address.",
        );
      }
    } catch (err: any) {
      const details = err?.response?.data?.details;
      const message =
        details?.email?._errors?.[0] ||
        err?.response?.data?.error ||
        'Failed to send verification';
      setError(message);
      setState('error');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      title="Verify email"
      onClose={handleClose}
      size="md"
      footer={
        <div className="verify-endpoint-modal__footer">
          <Button variant="secondary" onClick={handleClose}>
            {state === 'sent' ? 'Done' : 'Cancel'}
          </Button>
          {state !== 'sent' && (
            <Button
              onClick={handleSubmit as any}
              disabled={state === 'submitting'}
            >
              {state === 'submitting' ? 'Sending…' : 'Send link'}
            </Button>
          )}
        </div>
      }
    >
      {state !== 'sent' ? (
        <form className="verify-endpoint-modal" onSubmit={handleSubmit}>
          <p className="verify-endpoint-modal__hint">
            We'll send a one-time confirmation link to this address. Whoever
            opens the link completes the verification — no Klyx account
            required on their end.
          </p>
          <Input
            label="Email address"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            maxLength={254}
            required
            disabled={state === 'submitting'}
            error={state === 'error' ? error ?? undefined : undefined}
          />
        </form>
      ) : (
        <div className="verify-endpoint-modal">
          <p className="verify-endpoint-modal__success">
            ✓ Confirmation link sent to <strong>{sentTo}</strong>. It expires
            in 24 hours; the badge activates as soon as the link is opened.
          </p>
          {reissued && (
            <p className="verify-endpoint-modal__reissued">
              Note: this link replaces any previous challenge for this
              address.
            </p>
          )}
          <p className="verify-endpoint-modal__hint">
            You can close this window now. The verification status here
            updates on the next page load.
          </p>
        </div>
      )}
    </Modal>
  );
};

export default VerifyEmailModal;
